import * as React from "react";
import { createPortal } from "react-dom";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { Avatar } from "../../../ui/components/Avatar";
import {
  fetchCertificationModule,
  fetchCertificationModules,
  type CertificationTraining,
} from "../../lib/api/certifications";
import {
  sendOrgCertificationReminders,
  type OrgCertMember,
} from "../../lib/api/certifications-org";
import { getApiErrorMessage } from "../../lib/api/api-error";
import { useCertificationContentDefinitions } from "../../lib/i18n/useCertificationContentDefinitions";
import * as styles from "./OrganizationDetailPage.css";

const AUTO_VALUE = "";
/** Above Modal (9999) so the menu is not clipped or covered. */
const MENU_Z_INDEX = 10050;
const MENU_MAX_HEIGHT = 240;
const MENU_GAP = 4;

type TrainingOption = {
  value: string;
  label: string;
  moduleId: string;
  trainingId: string;
  thumbnailUrl: string | null;
};

function memberDisplayName(member: OrgCertMember): string {
  return member.name?.trim() || member.email;
}

function memberNameParts(member: OrgCertMember): {
  firstName: string;
  lastName: string;
} {
  if (member.firstName || member.lastName) {
    return {
      firstName: member.firstName ?? "",
      lastName: member.lastName ?? "",
    };
  }
  const parts = memberDisplayName(member).split(/\s+/);
  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" "),
  };
}

function TrainingPicker({
  value,
  options,
  disabled,
  onChange,
}: {
  value: string;
  options: TrainingOption[];
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = React.useState<React.CSSProperties | null>(
    null,
  );

  const updateMenuPosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP;
    const spaceAbove = rect.top - MENU_GAP;
    const openUpward =
      spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
    const maxHeight = Math.min(
      MENU_MAX_HEIGHT,
      Math.max(120, openUpward ? spaceAbove : spaceBelow),
    );
    setMenuStyle({
      position: "fixed",
      zIndex: MENU_Z_INDEX,
      left: rect.left,
      width: rect.width,
      maxHeight,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + MENU_GAP, top: "auto" }
        : { top: rect.bottom + MENU_GAP, bottom: "auto" }),
    });
  }, []);

  React.useEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, updateMenuPosition]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected =
    value === AUTO_VALUE
      ? null
      : (options.find((o) => o.value === value) ?? null);

  const toggleOpen = () => {
    if (open) {
      setOpen(false);
      return;
    }
    updateMenuPosition();
    setOpen(true);
  };

  const menu =
    open && menuStyle
      ? createPortal(
          <div
            ref={menuRef}
            className={styles.certReminderPickerMenu}
            role="listbox"
            style={menuStyle}
          >
            <button
              type="button"
              className={styles.certReminderPickerOption}
              role="option"
              aria-selected={value === AUTO_VALUE}
              onClick={() => {
                onChange(AUTO_VALUE);
                setOpen(false);
              }}
            >
              <span className={styles.certReminderThumbPlaceholder}>★</span>
              <span className={styles.certReminderOptionLabel}>
                Next incomplete training (automatic)
              </span>
            </button>
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={styles.certReminderPickerOption}
                role="option"
                aria-selected={value === opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.thumbnailUrl ? (
                  <img
                    src={opt.thumbnailUrl}
                    alt=""
                    className={styles.certReminderThumb}
                  />
                ) : (
                  <span className={styles.certReminderThumbPlaceholder}>▶</span>
                )}
                <span className={styles.certReminderOptionLabel}>
                  {opt.label}
                </span>
              </button>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={styles.certReminderPicker} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.certReminderPickerTrigger}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        {selected?.thumbnailUrl ? (
          <img
            src={selected.thumbnailUrl}
            alt=""
            className={styles.certReminderThumb}
          />
        ) : (
          <span className={styles.certReminderThumbPlaceholder}>
            {selected ? "▶" : "★"}
          </span>
        )}
        <span className={styles.certReminderOptionLabel}>
          {selected
            ? selected.label
            : "Next incomplete training (automatic)"}
        </span>
      </button>
      {menu}
    </div>
  );
}

export function SendCertificationReminderModal({
  open,
  setOpen,
  orgId,
  token,
  members,
  /** Optional per-user default picker values (`moduleId:trainingId`, or "" for auto). */
  initialOverrides,
  onSent,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  orgId: string;
  token: string | null;
  members: OrgCertMember[];
  initialOverrides?: Record<string, string>;
  onSent?: () => void;
}) {
  const [options, setOptions] = React.useState<TrainingOption[]>([]);
  const [optionsLoading, setOptionsLoading] = React.useState(false);
  /** userId → training option value ("" = auto next incomplete) */
  const [overrides, setOverrides] = React.useState<Record<string, string>>({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [resultSummary, setResultSummary] = React.useState<string | null>(null);
  const { resolve, ready: contentReady } = useCertificationContentDefinitions();

  React.useEffect(() => {
    if (!open) {
      setOverrides({});
      setError(null);
      setResultSummary(null);
      setLoading(false);
      return;
    }
    const initial: Record<string, string> = {};
    for (const m of members) {
      initial[m.userId] = initialOverrides?.[m.userId] ?? AUTO_VALUE;
    }
    setOverrides(initial);
  }, [open, members, initialOverrides]);

  React.useEffect(() => {
    if (!open || !token || !contentReady) return;
    let cancelled = false;
    setOptionsLoading(true);
    void (async () => {
      try {
        const modules = await fetchCertificationModules(token);
        const details = await Promise.all(
          modules
            .filter((m) => m.isActive)
            .map((m) => fetchCertificationModule(token, m.id).catch(() => null)),
        );
        if (cancelled) return;
        const next: TrainingOption[] = [];
        for (const mod of details) {
          if (!mod) continue;
          const moduleThumb =
            mod.heroImageUrl ?? mod.trophyImageUrl ?? null;
          const moduleLabel = resolve(mod.title) || mod.title;
          const trainings = [...(mod.trainings ?? [])].sort(
            (a: CertificationTraining, b: CertificationTraining) =>
              a.stepNumber - b.stepNumber || a.sortOrder - b.sortOrder,
          );
          for (const t of trainings) {
            const trainingLabel = resolve(t.title) || t.title;
            next.push({
              value: `${mod.id}:${t.id}`,
              label: `${moduleLabel} — ${trainingLabel}`,
              moduleId: mod.id,
              trainingId: t.id,
              thumbnailUrl: t.thumbnailUrl ?? moduleThumb,
            });
          }
        }
        setOptions(next);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, token, contentReady, resolve]);

  const handleSend = async () => {
    if (!token) {
      setError("Session expired. Please sign in again.");
      return;
    }
    if (members.length === 0) {
      setError("No members selected.");
      return;
    }
    setLoading(true);
    setError(null);
    setResultSummary(null);
    try {
      const bodyOverrides: Record<
        string,
        { moduleId?: string; trainingId?: string }
      > = {};
      for (const m of members) {
        const value = overrides[m.userId] ?? AUTO_VALUE;
        if (!value) continue;
        const opt = options.find((o) => o.value === value);
        if (opt) {
          bodyOverrides[m.userId] = {
            moduleId: opt.moduleId,
            trainingId: opt.trainingId,
          };
        }
      }
      const result = await sendOrgCertificationReminders(token, orgId, {
        userIds: members.map((m) => m.userId),
        ...(Object.keys(bodyOverrides).length > 0
          ? { overrides: bodyOverrides }
          : {}),
      });
      const failed = result.results.filter((r) => !r.ok);
      if (failed.length === 0) {
        setResultSummary(
          result.sent === 1
            ? "Reminder sent."
            : `${result.sent} reminders sent.`,
        );
        onSent?.();
      } else if (result.sent > 0) {
        setResultSummary(
          `Sent ${result.sent} of ${members.length}. ${failed.length} failed.`,
        );
        setError(
          failed
            .map((f) => {
              const name =
                members.find((m) => m.userId === f.userId)?.email ?? f.userId;
              return `${name}: ${f.error ?? "failed"}`;
            })
            .join(" "),
        );
        onSent?.();
      } else {
        setError(
          failed[0]?.error ??
            getApiErrorMessage(null, "Failed to send reminders"),
        );
      }
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to send reminders"));
    } finally {
      setLoading(false);
    }
  };

  const isBulk = members.length > 1;

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      minWidth="480px"
      maxWidth="560px"
      minHeight="auto"
    >
      <div className={styles.confirmModalBody}>
        <h3 className={styles.confirmModalTitle}>
          {isBulk
            ? "Send certification reminders"
            : "Send certification reminder"}
        </h3>
        <p className={styles.confirmModalText}>
          {isBulk
            ? `Send a reminder email to ${members.length} members.`
            : members[0]
              ? `Send a reminder email to ${memberDisplayName(members[0])}.`
              : "Select at least one member."}
        </p>

        {error && <Callout variant="error">{error}</Callout>}
        {resultSummary && <Callout variant="success">{resultSummary}</Callout>}

        <div className={styles.certReminderList}>
          {members.map((member) => {
            const { firstName, lastName } = memberNameParts(member);
            return (
              <div key={member.userId} className={styles.certReminderRow}>
                <div className={styles.certReminderIdentity}>
                  <Avatar
                    email={member.email}
                    firstName={firstName}
                    lastName={lastName}
                    image={member.avatarUrl ?? undefined}
                    size={36}
                  />
                  <div className={styles.certReminderIdentityText}>
                    <span className={styles.certReminderName}>
                      {memberDisplayName(member)}
                    </span>
                    <span className={styles.certReminderEmail}>
                      {member.email}
                    </span>
                  </div>
                </div>
                <TrainingPicker
                  value={overrides[member.userId] ?? AUTO_VALUE}
                  options={options}
                  disabled={loading || optionsLoading}
                  onChange={(next) =>
                    setOverrides((prev) => ({
                      ...prev,
                      [member.userId]: next,
                    }))
                  }
                />
              </div>
            );
          })}
        </div>

        <div className={styles.confirmModalActions}>
          <Button
            color="neutral"
            variant="outline"
            size="md"
            disabled={loading}
            onClick={() => setOpen(false)}
          >
            {resultSummary && !error ? "Close" : "Cancel"}
          </Button>
          {!(resultSummary && !error) && (
            <Button
              color="primary"
              variant="solid"
              size="md"
              loading={loading}
              disabled={members.length === 0 || optionsLoading}
              onClick={() => void handleSend()}
            >
              {isBulk ? `Send ${members.length} reminders` : "Send reminder"}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
